import {
  ImportCSVBookingField,
  ImportCSVErrorMessage,
  ImportCSVFormDataField,
  ValidateImportCSVFieldsArguments,
} from '@components/process-import-csv/src/process-import-csv.type';
import { emailValidator, isoCodeValidator } from '@utils/validations/text-validators';
import { TFunction } from 'i18next';

export const FIELD_QR_CODE = 'qrCode';
export const FIELD_FIRST_NAME = 'firstName';
export const FIELD_LAST_NAME = 'lastName';
export const FIELD_EMAIL = 'email';
export const FIELD_FULL_NAME = 'fullName';
export const FIELD_PHONE = 'phone';
export const FIELD_LANGUAGE = 'language';
export const FIELD_NATIONALITY = 'nationality';
export const FIELD_TAX_ID_PT = 'taxIdPt';

export const ERROR_EMPTY_MESSAGE = 'errors:allColumnEmptyRows';
export const ERROR_COLUMN_MESSAGE = 'errors:columnMessage';
export const ERROR_MESSAGE = 'errors:errorMessage';

export const isEmptyValidator = (values: string[]) => values.every((v: string) => v.trim() !== '' || v.trim() !== '0');

export const isEmailValidator = (values: string[]) => {
  const notEmailsList: number[] = [];

  values.forEach((value, index) => {
    if (!emailValidator.test(value) && value !== '') notEmailsList.push(index);
  });

  if (notEmailsList.length > 0) return notEmailsList;
  return true;
};

export const isIsoCodeValidator = (values: string[]): number[] | true => {
  const notIsoCodes = values.flatMap((value, index) => {
    if (value === '') return []; // Skip empty values

    const codes = value.includes(',') ? value.split(',') : [value];
    const invalidCodes = codes.filter((code) => !isoCodeValidator.test(code.trim().toLowerCase()));

    return invalidCodes.length > 0 ? [index] : [];
  });

  return notIsoCodes.length > 0 ? notIsoCodes : true;
};

export const bookingFields: ImportCSVBookingField[] = [
  {
    // No blocking rule: format problems in this column surface as non-blocking warnings via
    // createQrCodeIssueLists, so a false positive cannot lock the operator out of the import.
    id: FIELD_QR_CODE,
  },
  {
    id: FIELD_FIRST_NAME,
    validationRules: (values: string[]) => isEmptyValidator(values),
    error: ERROR_EMPTY_MESSAGE,
  },
  {
    id: FIELD_LAST_NAME,
    validationRules: (values: string[]) => isEmptyValidator(values),
    error: ERROR_EMPTY_MESSAGE,
  },
  {
    id: FIELD_FULL_NAME,
    validationRules: (values: string[]) => isEmptyValidator(values),
    error: ERROR_EMPTY_MESSAGE,
  },
  {
    id: FIELD_EMAIL,
    validationRules: (values: string[]) => isEmailValidator(values),
    error: 'errors:allColumnEmails',
  },
  {
    id: FIELD_PHONE,
    validationRules: (values: string[]) => !isEmptyValidator(values),
    error: ERROR_EMPTY_MESSAGE,
  },
  {
    id: FIELD_LANGUAGE,
    validationRules: (values: string[]) => isIsoCodeValidator(values),
    error: 'errors:onlyIsoCodes',
  },
  {
    id: FIELD_NATIONALITY,
    validationRules: (values: string[]) => !isEmptyValidator(values),
    error: ERROR_EMPTY_MESSAGE,
  },
];

const hasDuplicateError = (set: Set<ImportCSVErrorMessage>, error: ImportCSVErrorMessage) => {
  for (const item of set) {
    if (item.literal === error.literal && item.message === error.message) {
      return true;
    }
  }
  return false;
};

const createGeneralErrors = ({
  field,
  colIndex,
  columnSet,
  globalErrors,
}: {
  globalErrors: Set<ImportCSVErrorMessage>;
  columnSet: Set<string>;
  field: ImportCSVFormDataField;
  colIndex: number;
}) => {
  if (columnSet.has(field.field)) {
    globalErrors.add({
      literal: ERROR_COLUMN_MESSAGE,
      columnValue: colIndex,
      columnLabel: colIndex + 1,
      message: 'errors:generalErrorDuplicatedColumn',
    });
  } else if ((columnSet.has(FIELD_FIRST_NAME) || columnSet.has(FIELD_LAST_NAME)) && columnSet.has(FIELD_FULL_NAME)) {
    const nameFullNameError = { literal: ERROR_MESSAGE, message: 'errors:generalErrorNameFullName' };
    if (!hasDuplicateError(globalErrors, nameFullNameError)) globalErrors.add(nameFullNameError);
  } else if (field.field) {
    columnSet.add(field.field);
  }
};

const createColumnErrors = ({ rule, colIndex }: { rule?: string | boolean | number[]; colIndex: number }) => {
  const columnErrors: ImportCSVErrorMessage[] = [];
  if (rule) {
    if (typeof rule === 'string') {
      columnErrors.push({
        literal: ERROR_COLUMN_MESSAGE,
        columnValue: colIndex,
        columnLabel: colIndex + 1,
        message: rule,
      });
    } else if (typeof rule === 'object') {
      const numberArrayValidation = rule;
      if (numberArrayValidation.length !== 0) {
        let rows = '';
        numberArrayValidation.forEach((error, numIndex) => {
          rows += ` ${error + 1}${numberArrayValidation.length === numIndex + 1 ? '' : ','}`;
        });
        columnErrors.push({
          literal: 'errors:columnRowsMessage',
          columnValue: colIndex,
          columnLabel: colIndex + 1,
          message: rows,
        });
      }
    }
  }
  return columnErrors;
};

export const createGeneralErrorsMessage = (errors: Set<ImportCSVErrorMessage>, t: TFunction) => {
  let message = '';
  let idx = 0;
  errors.forEach((error: ImportCSVErrorMessage) => {
    idx++;
    message = `${message} ${t(error.literal, {
      message: t(error.message as string),
      columnValue: error.columnLabel,
    })} ${idx === 0 ? '' : '<br/>'}`;
  });
  return message;
};

export const validateFields = ({ formData, processedColumns }: ValidateImportCSVFieldsArguments) => {
  if (!formData) return;
  const generalErrors = new Set<ImportCSVErrorMessage>();
  const columnSet = new Set<string>();
  const columnErrors: ImportCSVErrorMessage[] = [];
  const qrCodeColumnIndex = formData.findIndex((col) => col?.field === FIELD_QR_CODE);
  if (qrCodeColumnIndex === -1) generalErrors.add({ literal: ERROR_MESSAGE, message: 'errors:generalErrorQrCode' });

  formData.forEach((field, colIndex) => {
    const columnData = processedColumns[colIndex];
    const validationRule = bookingFields.find((bookingField) => bookingField.id === field.field)?.validationRules;

    createGeneralErrors({ field, colIndex, columnSet, globalErrors: generalErrors });
    columnErrors.push(...createColumnErrors({ colIndex, rule: validationRule?.(columnData) }));
  });
  const errorData = { generalErrors, columnErrors, qrCodeColumnIndex };
  return errorData;
};
